import CreateButton from "../components/CreateButton"
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import api from "../api";
import { ReactComponent as CopyIcon } from "../assets/images/icons/ic-copy.svg";
import { ReactComponent as DeleteIcon } from "../assets/images/icons/ic-delete.svg";
import StatusBadge from "../components/StatusBadge";
import TextInput from "../components/TextInput";
import { getNextEvent, parseEventDate } from "../utils/eventDates";
import Card from "../components/cards/Card";
import HeaderFooterLayout from '../components/layouts/HeaderFooterLayout';
import MeatBallsMenu from "../components/MeatBallsMenu";
import Table from "../components/Table";
import styles from './Events.module.css';
import AlertDialog from "../components/dialogs/AlertDialog";

const Events = () => {

    const { t } = useTranslation();
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true)
    const [query, setQuery] = useState('');
    const [loadError, setLoadError] = useState(false);
    const [now, setNow] = useState(() => Date.now());
    const [alert, setAlert] = useState({ open: false, title: '', text: '' })

    useEffect(() => {

        const getEvents = async () => {
            setLoading(true);
            try {
                const events = await api.get('/admin/events');
                setEvents(events);
            } catch (error) {
                console.error(error);
                setLoadError(true);
            }
            setLoading(false)
        }

        getEvents();

    }, []);

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 30000);
        return () => clearInterval(timer);
    }, []);

    const nextEvent = getNextEvent(events, now);
    const visibleEvents = events.filter(event =>
        `${event.name || ''} ${event.description || ''}`.toLocaleLowerCase('it').includes(query.trim().toLocaleLowerCase('it'))
    );
    const formatDate = value => {
        const date = parseEventDate(value);
        return date ? new Intl.DateTimeFormat('it-IT', {
            dateStyle: 'long', timeStyle: 'short'
        }).format(date) : 'Data da definire';
    };

    const deleteEvent = async (eventId) => {
        try {
            await api.delete(`/admin/events/${eventId}`);
            setEvents((prev) => prev.filter((e) => e.id !== eventId));
        } catch (error) {
            console.error(error);
        }
    }

    const duplicateEvent = async (eventId) => {
        try {
            const duplicated = await api.post(`/admin/events/${eventId}/duplicate`);
            setEvents((prev) => [...prev, duplicated]);
        } catch (error) {
            console.error(error);
        }
    }

    const getStatusColor = (status) => {
        switch (status) {
            case 'public':
                return 'var(--sf-green)'
            case 'draft':
                return 'var(--secondary)'
            case 'hidden':
                return 'var(--sf-red)'
            default:
                return 'var(--sf-grey)'
        }
    }

    const getEventActions = (event) => {
        if (!event) return []
        const actions = [
            {
                label: "Duplica",
                icon: CopyIcon,
                onClick: () => {
                    setAlert({
                        open: true,
                        title: `Duplicare ${event.name}?`,
                        text: 'Verrà creata una copia dell\'evento in stato bozza.',
                        actions: [
                            {
                                label: 'DUPLICA',
                                onClick: async () => {
                                    await duplicateEvent(event.id)
                                    setAlert((prev) => {
                                        prev.open = false
                                        return { ...prev }
                                    })
                                }
                            }
                        ]
                    })
                }
            }
        ]
        if (event.status === 'draft') {
            actions.push(
                {
                    label: "Elimina",
                    icon: DeleteIcon,
                    onClick: () => {
                        setAlert({
                            open: true,
                            title: `Eliminare ${event.name}?`,
                            text: 'L\'operazione non potrà essere annullata',
                            actions: [
                                {
                                    label: 'ELIMINA',
                                    onClick: async () => {
                                        await deleteEvent(event.id)
                                        setAlert((prev) => {
                                            prev.open = false
                                            return { ...prev }
                                        })
                                    },
                                    color: 'var(--sf-red)'
                                }
                            ]
                        })
                    },
                    color: 'var(--sf-red)'
                }
            )
        }
        return actions
    }

    return (
        <HeaderFooterLayout>
            <div className={styles.container}>
                <div className={styles.section}>
                    <div className={styles.sectionInner}>
                        <div className={styles.header}>
                            <div><p className={styles.eyebrow}>Organizzazione</p><h1>Eventi</h1><p>Gestisci gli eventi e tieni sotto controllo i prossimi appuntamenti.</p></div>
                            <CreateButton style={{ padding: '0.5rem 1rem' }} onClick={() => { navigate('/events/create/editor') }}>
                                Aggiungi

                            </CreateButton>
                        </div>
                        {!loading && !loadError && (
                            <section className={styles.featured} aria-label="Prossimo evento">
                                {nextEvent ? <>
                                    {nextEvent.thumbnail && <img className={styles.featuredImage} src={nextEvent.thumbnail} alt="" />}
                                    <div className={styles.featuredContent}>
                                        <p className={styles.eyebrow}>Prossimo evento</p>
                                        <h2>{nextEvent.name}</h2>
                                        <time dateTime={parseEventDate(nextEvent.starts_at).toISOString()}>{formatDate(nextEvent.starts_at)}</time>
                                        <StatusBadge appearance="soft" label={t(`status.${nextEvent.status}`)} color={getStatusColor(nextEvent.status)} />
                                    </div>
                                    <Link className={styles.eventLink} to={`/events/${nextEvent.id}`}>Gestisci evento →</Link>
                                </> : <div className={styles.featuredContent}><p className={styles.eyebrow}>Prossimo evento</p><h2>Nessun evento in programma</h2><p>I prossimi eventi con una data di inizio appariranno qui.</p></div>}
                            </section>
                        )}
                        {loadError && <p role="alert">Impossibile caricare gli eventi. Ricarica la pagina per riprovare.</p>}
                        <Card appearance="modern" integratedTable title="Elenco eventi" actions={<span className={styles.resultCount} role="status">{loading ? 'Caricamento…' : `${visibleEvents.length} eventi`}</span>}>
                            <div className={styles.search}><TextInput appearance="modern" type="search" value={query} onKeyUp={setQuery} placeholder="Cerca un evento" /></div>
                            {!loading && !loadError && visibleEvents.length === 0 && <p className={styles.empty} role="status">{query ? 'Nessun evento trovato per questa ricerca.' : 'Non ci sono ancora eventi.'}</p>}
                            <Table
                                appearance="minimal"
                                style={{ borderRadius: 0 }}
                                loading={loading}
                                columns={[
                                    {
                                        key: 'id',
                                        label: 'ID',
                                        type: 'number',
                                        pinned: false
                                    },
                                    {
                                        key: 'thumbnail',
                                        label: 'COPERTINA',
                                        type: 'image',
                                        pinned: false
                                    },
                                    {
                                        key: 'name',
                                        label: 'NOME',
                                        type: 'string',
                                        pinned: false
                                    },
                                    {
                                        key: 'starts_at',
                                        label: 'DATA EVENTO',
                                        type: 'custom',
                                        pinned: false
                                    },
                                    {
                                        key: 'description',
                                        label: 'DESCRIZIONE',
                                        type: 'custom',
                                        pinned: false
                                    },
                                    {
                                        key: 'status',
                                        label: 'STATO',
                                        type: 'custom',
                                        pinned: false
                                    },
                                    {
                                        key: 'actions',
                                        label: 'AZIONI',
                                        type: 'custom',
                                        pinned: false
                                    }
                                ]}
                                elements={
                                    visibleEvents.map(e => ({
                                        id: e.id,
                                        name: e.name,
                                        starts_at: { value: parseEventDate(e.starts_at)?.getTime() || 0, type: 'number', component: <span>{formatDate(e.starts_at)}</span> },
                                        description: {value: e.description, type: 'string', component: <div className={styles.description}>{e.description}</div>},
                                        thumbnail: <img src={e.thumbnail} alt="" className={styles.thumbnail} />,
                                        status: { value: e.status, type: 'string', component: <StatusBadge appearance="soft" label={`${t(`status.${e.status}`)}`} color={getStatusColor(e.status)} /> },
                                        actions: <MeatBallsMenu style={{ padding: '0.5rem' }} actions={getEventActions(e)} />,
                                        href: `/events/${e.id}`
                                    }))}
                                defaultSort={{ key: 'starts_at', direction: 'desc' }}
                            />
                        </Card>
                    </div>
                </div>
            </div>
            <AlertDialog open={alert.open} title={alert.title} text={alert.text} actions={alert.actions} onClose={() => {
                setAlert((prev) => {
                    prev.open = false
                    return { ...prev }
                })
            }} />
        </HeaderFooterLayout >
    )

}

export default Events;
